import { useEffect, useRef, useState } from "react";

/**
 * The reference's text/digit flip animations, shared by the cart drawer and the
 * cart page. Both replay on CHANGE by remounting (`key` bump) — the first render
 * mounts without the animation class, so nothing animates on page load. The
 * keyframes and the `[data-dnd-flip]`/`[data-dnd-swap]` display rules live in
 * `design-system/components/keyframes.js`; the data attribute is what makes the
 * span `inline-block`, without which the transform silently does nothing.
 */

/** Whole-text flip (`dndDigitFlip`), or `swap` for the softer `dndTextSwap`. */
export function FlipText({ text, swap = false, className = "" }) {
  const [playKey, setPlayKey] = useState(0);
  const prev = useRef(text);
  useEffect(() => {
    if (prev.current !== text) {
      prev.current = text;
      setPlayKey((k) => k + 1);
    }
  }, [text]);
  const playClass =
    playKey > 0 ? (swap ? "dnd-swap-play" : "dnd-flip-play") : "";
  const dataAttr = swap ? { "data-dnd-swap": "1" } : { "data-dnd-flip": "1" };
  return (
    <span
      key={playKey}
      className={`${playClass} ${className}`.trim()}
      {...dataAttr}
    >
      {text}
    </span>
  );
}

/**
 * Per-digit flip for money amounts: every digit replays together, the last two
 * (cents) trail by 70/140ms — upstream's `_digits`. The `cart-total` class also
 * superscripts the leading `$` (rule in keyframes.js).
 */
export function FlipAmount({ text, className = "" }) {
  const [playKey, setPlayKey] = useState(0);
  const prev = useRef(text);
  useEffect(() => {
    if (prev.current !== text) {
      prev.current = text;
      setPlayKey((k) => k + 1);
    }
  }, [text]);
  const chars = String(text).split("");
  return (
    <b
      key={playKey}
      data-dnd-flip="1"
      className={`cart-total tabular-nums ${
        playKey > 0 ? "dnd-flip-play" : ""
      } ${className}`.trim()}
    >
      {chars.map((ch, i) => (
        <span
          key={i}
          className="dnd-digit"
          style={{
            "--fd": `${i === chars.length - 2 ? 70 : i === chars.length - 1 ? 140 : 0}ms`,
          }}
        >
          {ch}
        </span>
      ))}
    </b>
  );
}
